// Shared gear/item math — the ONE place item-level stat scaling, rarity colour,
// slot labels and upgrade/repair pricing live. Imported by Inventory.vue,
// ItemModal.vue, HeroCard.vue and HeroDossier.vue so none of them duplicate
// this formula (it must always match the backend's combat math exactly).
import type { Artifact, ArtifactCurrency, ArtifactRarity, EquipSlot } from './inventory-api';
import { t } from '../i18n';

/** Slot → display label (Dominion-style flavor names), translated at access time. */
export const SLOT_LABELS: Record<EquipSlot, string> = Object.defineProperties(
  {} as Record<EquipSlot, string>,
  Object.fromEntries(
    (['weapon', 'helmet', 'armor', 'boots', 'ring', 'amulet'] as EquipSlot[]).map((s) => [
      s, { get: () => t(`items.slots.${s}`), enumerable: true },
    ]),
  ),
);

/** Rarity ladder → accent hex (grey → green → violet → orange → gold). Drives
 *  card accent rails, glows, name colour and rarity tags everywhere. */
export const RARITY_HEX: Record<ArtifactRarity, string> = {
  COMMON: '#9a927f',
  UNCOMMON: '#6bbf59',
  RARE: '#b98cff',
  EPIC: '#e0872f',
  LEGENDARY: '#f0cf62',
  // Icy pale cyan — non-colliding with gold LEGENDARY/orange EPIC/violet RARE
  // and with --color-gold-bright (rune-price text), see item-expansion-14-08.md §2.
  MYTHIC: '#8ee9ff',
};

export const MAX_ARTIFACT_LEVEL = 5;

/** Cash cost to upgrade FROM a given level (mirrors backend UPGRADE_COSTS in
 *  backend/src/game/inventory/inventory.constants.ts). */
export const UPGRADE_COST: Record<number, number> = { 1: 50, 2: 150, 3: 400, 4: 1000 };

/**
 * Level-scaled item stat (MUST match backend scaledArtifactStat in
 * backend/src/game/battle/battle-engine.ts): +10% of base per level above 1,
 * floored at +1 per level — the flat rounded multiplier plateaued small stats
 * (base 5 went 5,5,6,7,7) and made the last upgrade a no-op.
 */
export function scaledStat(stat: number, level: number): number {
  if (stat <= 0) return 0;
  return stat + Math.max(1, Math.round(stat * 0.1)) * (level - 1);
}

/**
 * The level the stat scaling actually runs on: the grade the item was born at
 * plus every sharpening step paid for since.
 *
 * `powerLevel` tracks the foe a drop came off (a level-15 enemy yields tier 9);
 * `level` is the 1..MAX_ARTIFACT_LEVEL track. One column held both until
 * 2026-08-18, which is why drops rendered as "LV 9 / 5" — a number above its own
 * maximum, unupgradeable on arrival, and impossible to compare against a 5/5 of
 * a different rarity. MUST stay in lockstep with backend battle-engine.ts
 * effectiveArtifactLevel.
 */
export function effectiveLevel(a: { powerLevel: number; level: number }): number {
  return Math.max(1, a.powerLevel) + Math.max(1, a.level) - 1;
}

export type ItemStatKey = 'attack' | 'defense' | 'magic' | 'speed' | 'hp';

/** Display metadata for the four combat stats — label + accent used for bars,
 *  dossier tiles, item stat rows and gear-bonus rows across the whole page. */
// `desc` lines are the player-facing stat explanations (QA punch-list 07.08
// item 6) and each claim is grounded in the combat math: heroDmg = attack −
// foe defense (mobfight.combat.ts), magic folds into attack at 50%
// (mobfight.service.ts heroAttackWith). Reflexes has NO combat consumer today —
// its copy promises nothing; if it ever gets a role, update the line here.
// NOTE the `short` for attack is DMG, not FPR — 'Firepower' was the mafia
// build's stat and its return is a ticket regression (there are no firearms).
export const STAT_META: Record<'attack' | 'defense' | 'magic' | 'speed', { label: string; short: string; color: string; desc: string }> = {
  attack: { get label() { return t('items.stats.firepower'); }, get short() { return t('items.stats.fpr'); }, color: '#c0504d', get desc() { return t('items.stats.firepowerDesc'); } },
  defense: { get label() { return t('items.stats.toughness'); }, get short() { return t('items.stats.tuf'); }, color: '#7aa0c8', get desc() { return t('items.stats.toughnessDesc'); } },
  magic: { get label() { return t('items.stats.cunning'); }, get short() { return t('items.stats.cun'); }, color: '#b98cff', get desc() { return t('items.stats.cunningDesc'); } },
  speed: { get label() { return t('items.stats.reflexes'); }, get short() { return t('items.stats.rfx'); }, color: '#7fbf8f', get desc() { return t('items.stats.reflexesDesc'); } },
};

/** All five level-scaled stat contributions of an artifact, keyed for iteration. */
export function scaledStats(a: Artifact): Record<ItemStatKey, number> {
  const lvl = effectiveLevel(a);
  return {
    attack: scaledStat(a.attack, lvl),
    defense: scaledStat(a.defense, lvl),
    magic: scaledStat(a.magic, lvl),
    speed: scaledStat(a.speed, lvl),
    hp: scaledStat(a.hp, lvl),
  };
}

export interface StatCompareRow {
  key: ItemStatKey;
  label: string;
  candidate: number;
  equipped: number;
  delta: number;
}

/**
 * Compare a candidate artifact's level-scaled stats against whatever currently
 * occupies its slot (or null if the slot is empty). Only rows where either side
 * is nonzero are returned. Powers the "compare vs equipped" delta list in the
 * item detail modal.
 */
export function compareStats(candidate: Artifact, equipped: Artifact | null): StatCompareRow[] {
  const cand = scaledStats(candidate);
  const equip = equipped ? scaledStats(equipped) : { attack: 0, defense: 0, magic: 0, speed: 0, hp: 0 };
  const labels: Record<ItemStatKey, string> = {
    attack: STAT_META.attack.label, defense: STAT_META.defense.label,
    magic: STAT_META.magic.label, speed: STAT_META.speed.label, hp: t('items.stats.health'),
  };
  const keys: ItemStatKey[] = ['attack', 'defense', 'magic', 'speed', 'hp'];
  return keys
    .filter((k) => cand[k] !== 0 || equip[k] !== 0)
    .map((k) => ({ key: k, label: labels[k], candidate: cand[k], equipped: equip[k], delta: cand[k] - equip[k] }));
}

/** True when a breakable item is worn (below max) or fully broken. */
export function needsRepair(a: Artifact): boolean {
  if (!a.isBreakable || a.maxDurability == null) return false;
  return a.isBroken || (a.durability ?? a.maxDurability) < a.maxDurability;
}

/** Durability as a 0–100 percentage (broken → 0), for the wear bar. */
export function durabilityPct(a: Artifact): number {
  if (!a.isBreakable || a.maxDurability == null) return 100;
  if (a.isBroken) return 0;
  return Math.round(((a.durability ?? a.maxDurability) / a.maxDurability) * 100);
}

const RARITY_REPAIR_BASE: Record<ArtifactRarity, number> = {
  COMMON: 20, UNCOMMON: 45, RARE: 90, EPIC: 180, LEGENDARY: 400,
  // Live since 2026-08-18: Mythic is shop-only, and bought gear wears now too.
  MYTHIC: 900,
};

/**
 * Cash cost to fully repair an item — a client-side PREVIEW only (mirrors
 * backend/src/game/loot/loot-generator.ts:repairCost exactly). The server
 * remains the source of truth and re-derives this cost itself on /gear/repair;
 * this is purely so the modal can show a price before the player commits.
 */
export function repairCostPreview(a: Artifact): number {
  if (!a.isBreakable || a.maxDurability == null) return 0;
  const current = a.durability ?? a.maxDurability;
  const missing = a.maxDurability - current;
  if (missing <= 0 && !a.isBroken) return 0;
  const effectiveMissing = a.isBroken ? a.maxDurability : missing;
  const base = RARITY_REPAIR_BASE[a.rarity];
  const levelFactor = 1 + Math.max(0, effectiveLevel(a) - 1) * 0.1;
  const wearFraction = Math.min(1, effectiveMissing / a.maxDurability);
  return Math.max(1, Math.ceil(base * levelFactor * wearFraction));
}

/** Rarity → fair-value anchor (mirrors fairValue() in backend market-pricing.ts). */
const FAIR_VALUE_BASE: Record<ArtifactRarity, number> = {
  COMMON: 40, UNCOMMON: 100, RARE: 300, EPIC: 800, LEGENDARY: 2000, MYTHIC: 5000,
};

/** Quick-sell rates by the item's own currency (backend QUICKSELL_RATE[_PREMIUM]). */
const QUICKSELL_RATE = { SOFT: 0.5, PREMIUM: 0.7 } as const;

/**
 * Quick-sell PREVIEW — mirrors MarketService.sellValue + quickSellRate: the
 * item's street price expressed IN ITS OWN CURRENCY, at 50% for silver-priced
 * gear and 70% for rune-priced gear. The two units are never converted, so the
 * caller must render the coin that comes back with the number.
 *
 * The server re-derives the real payout on /market/quicksell.
 */
export function quickSellPreview(a: Artifact): { payout: number; currency: ArtifactCurrency } {
  const currency = a.currency ?? 'SOFT';
  const street = a.currentPrice > 0
    ? a.currentPrice
    : a.basePrice > 0
      ? a.basePrice
      : Math.max(1, Math.round(
        FAIR_VALUE_BASE[a.rarity]
        + Math.max(0, a.attack + a.defense + a.magic + a.speed + a.hp) * 5,
      ));
  return { payout: Math.max(1, Math.round(street * QUICKSELL_RATE[currency])), currency };
}
